import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiChevronUp, FiEdit2, FiTrash2, FiFileText, FiUser } from 'react-icons/fi';
import { absoluteUrl, VISIT_TYPE_TONES, visitPath } from 'modules/Media/mediaUtils';
// Direct path, not the utils/custom barrel (which pulls the store in).
import { formatTimeWithZone } from 'utils/custom/formatDateTimeZone';
import { displayReason, noteMoment, visitBadgeLabel } from './noteUtils';
import NoteEditor from './NoteEditor';
import * as Styled from './notes.styles';

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|heic)$/i;

// How many lines of a note's text show before "Show more".
const BODY_PREVIEW_LINES = 6;

const initials = (name) => {
  if (!name) return '';
  // Authors without a name come back as their email — initial the local part only.
  const words = name
    .split('@')[0]
    .split(/[\s._-]+/)
    .filter(Boolean);
  return words
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
};

const NoteMedia = ({ media }) => (
  <Styled.MediaRow>
    {media.map((file) => {
      const href = absoluteUrl(file.url);
      const name = file.file_name || 'file';
      return IMAGE_EXT.test(name) ? (
        <Styled.Thumb key={file.id} href={href} target="_blank" rel="noopener noreferrer" title={name}>
          <img src={href} alt={name} loading="lazy" />
        </Styled.Thumb>
      ) : (
        <Styled.FileChip key={file.id} href={href} target="_blank" rel="noopener noreferrer" title={name}>
          <FiFileText aria-hidden />
          <span>{name}</span>
        </Styled.FileChip>
      );
    })}
  </Styled.MediaRow>
);

NoteMedia.propTypes = {
  media: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, url: PropTypes.string, file_name: PropTypes.string }),
  ).isRequired,
};

/**
 * A note's text, folded to BODY_PREVIEW_LINES. "Show more" appears only when the text really runs past
 * them — measured, not guessed from its length, since line breaks and the card's width decide it — and
 * is re-checked as the card resizes.
 */
const NoteBody = ({ text }) => {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return undefined;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    check();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Checked on the next frame: a change made inside the observer's delivery would report back in the
    // same delivery (the "ResizeObserver loop" error).
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(check);
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [text, expanded]);

  const canToggle = expanded || overflows;
  return (
    <>
      <Styled.Body ref={ref} $clamped={!expanded} $lines={BODY_PREVIEW_LINES}>
        {text}
      </Styled.Body>
      {canToggle && (
        <Styled.MoreToggle type="button" aria-expanded={expanded} onClick={() => setExpanded((open) => !open)}>
          {expanded ? 'Show less' : 'Show more'}
          {expanded ? <FiChevronUp aria-hidden /> : <FiChevronDown aria-hidden />}
        </Styled.MoreToggle>
      )}
    </>
  );
};

NoteBody.propTypes = { text: PropTypes.string };
NoteBody.defaultProps = { text: '' };

/**
 * One note — manual or visit-derived. A visit note links to its visit and may carry the files the
 * form paired with the note question; only a manual note can show edit / delete, and only when the
 * backend says this user may (`can_edit` is author-only, `can_delete` is author or Account Manager+).
 */
const NoteItem = ({
  note,
  timeZone,
  hideVisitContext,
  editHref,
  editing,
  busy,
  highlighted,
  onEdit,
  onCancelEdit,
  onSave,
  onDelete,
}) => {
  const { id, source, date, author, reason, text, visit_id: visitId, visit_type: visitType, media } = note;
  const isVisit = source === 'visit';
  // On the visit's own page its type badge and "Open visit" link would only point back at itself.
  const visitHref = isVisit && !hideVisitContext ? visitPath({ visitId, visitType }) : null;
  const visitLabel = hideVisitContext ? null : visitBadgeLabel(note);
  const shownReason = displayReason(note);
  const when = noteMoment(date, timeZone);
  const authorInitials = initials(author);

  return (
    <Styled.Item
      // Manual notes can be linked to (`?note=<id>`); the linked one is highlighted and takes focus.
      id={id != null ? `retailer-note-${id}` : undefined}
      tabIndex={highlighted ? -1 : undefined}
      $highlighted={highlighted}
      aria-busy={busy || undefined}
    >
      <Styled.Avatar aria-hidden>{authorInitials || <FiUser />}</Styled.Avatar>
      <Styled.Main>
        <Styled.Header>
          <Styled.HeaderText>
            <Styled.Author>{author || 'Deleted user'}</Styled.Author>
            {when && (
              // In the account's time zone, named as the visit page's Submitted time names it:
              // "10/05/2026 · 10:17 AM US/Eastern (UTC -04:00)".
              <Styled.Muted as="time" dateTime={when.toISOString()}>
                {timeZone
                  ? `${when.format('MM/DD/YYYY')} · ${formatTimeWithZone(date, timeZone)}`
                  : when.format('MM/DD/YYYY · HH:mm')}
              </Styled.Muted>
            )}
            {visitLabel && <Styled.VisitTag tone={VISIT_TYPE_TONES[visitType]}>{visitLabel}</Styled.VisitTag>}
            {visitHref && (
              <Styled.VisitLink as={Link} to={visitHref}>
                Open visit
              </Styled.VisitLink>
            )}
          </Styled.HeaderText>
          {!editing && (note.can_edit || note.can_delete || editHref) && (
            <Styled.Actions>
              {/* A visit note is edited in its visit's form: the pencil opens Edit Visit at the
                  question the note is stored in. Same pencil as a manual note's, as a link. */}
              {editHref && (
                <Styled.IconButton as={Link} to={editHref} aria-label="Edit note in the visit form" title="Edit">
                  <FiEdit2 />
                </Styled.IconButton>
              )}
              {note.can_edit && (
                <Styled.IconButton type="button" onClick={onEdit} disabled={busy} aria-label="Edit note" title="Edit">
                  <FiEdit2 />
                </Styled.IconButton>
              )}
              {note.can_delete && (
                <Styled.IconButton
                  type="button"
                  onClick={onDelete}
                  disabled={busy}
                  aria-label="Delete note"
                  title="Delete"
                >
                  <FiTrash2 />
                </Styled.IconButton>
              )}
            </Styled.Actions>
          )}
        </Styled.Header>

        {editing ? (
          <NoteEditor
            idPrefix={`note-${id}`}
            initialReason={reason || ''}
            initialText={text || ''}
            onSubmit={onSave}
            onCancel={onCancelEdit}
          />
        ) : (
          <>
            {shownReason && <Styled.Reason>{shownReason}</Styled.Reason>}
            <NoteBody text={text} />
            {media?.length > 0 && <NoteMedia media={media} />}
          </>
        )}
      </Styled.Main>
    </Styled.Item>
  );
};

NoteItem.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.number,
    source: PropTypes.oneOf(['manual', 'visit']).isRequired,
    date: PropTypes.string,
    author: PropTypes.string,
    reason: PropTypes.string,
    text: PropTypes.string,
    visit_id: PropTypes.number,
    visit_type: PropTypes.number,
    media: PropTypes.arrayOf(PropTypes.object),
    can_edit: PropTypes.bool,
    can_delete: PropTypes.bool,
  }).isRequired,
  // The account's time zone (AuthEntity getTimeZone), e.g. "US/Eastern".
  timeZone: PropTypes.string,
  // On the visit's own page: leave out the visit type badge and the "Open visit" link.
  hideVisitContext: PropTypes.bool,
  // A visit note's edit link: the Edit Visit page, opened at the note's question.
  editHref: PropTypes.string,
  editing: PropTypes.bool,
  busy: PropTypes.bool,
  highlighted: PropTypes.bool,
  onEdit: PropTypes.func,
  onCancelEdit: PropTypes.func,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
};

NoteItem.defaultProps = {
  timeZone: null,
  hideVisitContext: false,
  editHref: null,
  editing: false,
  busy: false,
  highlighted: false,
  onEdit: () => {},
  onCancelEdit: () => {},
  onSave: async () => false,
  onDelete: () => {},
};

export default NoteItem;
